import React, { useState } from "react";
import { api } from "../lib/api";
import { Shield, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * IdentityVerification — Provider Dashboard card driving Level 1 of the trust stack.
 *
 * Real implementation: Stripe Identity verification session. In MVP we stub the
 * flow end-to-end (start → "complete" callback) so the UI shows the full journey.
 * NO sensitive PII is stored — only status + Stripe verification session ref id.
 */
export default function IdentityVerification({ provider, onChange }) {
  const verified = !!provider?.identity_verified;
  const status = provider?.identity_status || "Not Started";
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/providers/identity/start");
      // STUB: in production the user would be redirected to Stripe's hosted
      // verification page. For MVP we simulate the success callback inline.
      await api.post("/providers/identity/complete", { success: true });
      toast.success("Identity verified · Badge unlocked");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start verification");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-3xl card-luxury-elevated p-6"
      data-testid="identity-verification-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#00AEEF] font-semibold">
            Trust · Level 1
          </div>
          <h3 className="font-serif text-2xl mt-2 flex items-center gap-2">
            <Shield className="text-[#00AEEF]" size={20} /> Identity Verified
          </h3>
          <p className="text-sm text-[#071A2D]/70 mt-2 max-w-md leading-relaxed">
            Verify your identity through our secure verification partner. TREL
            never stores your ID — only your verification status. Free.
          </p>
        </div>
        {verified ? (
          <span className="rounded-full px-3 py-1 text-xs font-semibold bg-[#00AEEF] text-[#FFF8EE] flex items-center gap-1">
            <Check size={12} /> Verified
          </span>
        ) : (
          <span className="rounded-full px-3 py-1 text-xs bg-[#071A2D]/5 text-[#071A2D]/70">
            {status}
          </span>
        )}
      </div>

      {!verified && (
        <div className="mt-5">
          <button
            onClick={start}
            disabled={busy}
            data-testid="identity-start-btn"
            className="btn-coral inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
            {busy ? "Verifying…" : "Start identity verification"}
          </button>
          <div className="text-[11px] text-[#071A2D]/55 mt-3">
            Powered by a secure third-party identity provider. Your documents
            never touch TREL servers.
          </div>
        </div>
      )}

      {verified && (
        <div className="mt-5 text-xs text-[#064E3B] flex items-center gap-2">
          <Check size={14} /> Trust Level 1 unlocked · Identity verification on file
        </div>
      )}
    </div>
  );
}
